// src/new/components/monitor/TriageChat/TriageChatContainer.tsx
import React from 'react';
import { useSelector } from 'react-redux';
import { getAccessToken } from 'js/reducers/session';
import { API_VEDA_AI_URL, API_EXPRESS_CODE_URL } from 'js/constants/api';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import { TriageContext } from './triageContext';
import { useTriageChat } from './useTriageChat';
import { createVedaBackend } from './vedaBackend';
import { createExpressBackend } from './expressBackend';
import { TriageChat } from './TriageChat';

export const TriageChatContainer: React.FC<{
  context: TriageContext;
  /** False for a click-opened session (see TriageChatProvider). Defaults to
   *  `true` so the standalone `/monitor/triage-chat` route (TriageChatRoute),
   *  which renders this directly without passing the prop, keeps today's
   *  auto-seed-on-mount behavior unchanged. */
  autoSeed?: boolean;
}> = ({ context, autoSeed = true }) => {
  const token = useSelector(getAccessToken) as string | null;
  // `UI_AI_TRIAGE_CHAT` gating is intentionally left to the parent
  // `TriageChatProvider`, which already checks that flag before mounting
  // this component — no need to re-check it here.
  const useExpressBackend = useFeatureFlag('UI_TRIAGE_EXPRESS_BACKEND');
  const baseUrl = useExpressBackend ? API_EXPRESS_CODE_URL : API_VEDA_AI_URL;

  if (!baseUrl) {
    const missing = useExpressBackend ? 'express-code' : 'VEDA_AI';
    return (
      <TriageChat
        context={context}
        messages={[]}
        connected={false}
        streaming={false}
        error={`Triage chat is not configured (missing ${missing} URL).`}
        onSend={() => {}}
      />
    );
  }
  if (!token) {
    return (
      <TriageChat
        context={context}
        messages={[]}
        connected={false}
        streaming={false}
        error="You must be signed in to use triage chat."
        onSend={() => {}}
      />
    );
  }

  return (
    // `key` forces a fresh mount if the backend selection flips while this
    // is on screen (GrowthBook flags can update live, without a page reload).
    // Without it, `BoundTriageChat` would recompute `backend` but keep the
    // old `sessionId` from `useTriageChat`'s state, wiring it into the wrong
    // transport hook.
    <BoundTriageChat
      key={useExpressBackend ? 'express' : 'veda'}
      baseUrl={baseUrl}
      token={token}
      context={context}
      useExpressBackend={useExpressBackend}
      autoSeed={autoSeed}
    />
  );
};

const BoundTriageChat: React.FC<{
  baseUrl: string;
  token: string;
  context: TriageContext;
  useExpressBackend: boolean;
  autoSeed: boolean;
}> = ({ baseUrl, token, context, useExpressBackend, autoSeed }) => {
  const backend = React.useMemo(
    () =>
      useExpressBackend ? createExpressBackend(baseUrl, token) : createVedaBackend(baseUrl, token),
    [useExpressBackend, baseUrl, token],
  );
  const chat = useTriageChat({ baseUrl, token, context, backend, autoSeed });
  return (
    <TriageChat
      context={context}
      messages={chat.messages}
      connected={chat.connected}
      streaming={chat.streaming}
      error={chat.error}
      onSend={chat.send}
      onStop={chat.stop}
      waitingForReady={chat.waitingForReady}
      reconnecting={chat.reconnecting}
      blockSendWhileStreaming={useExpressBackend}
      autoSeed={autoSeed}
    />
  );
};
